import type { StateMachine } from '@xstate/fsm';
import { interpret, createMachine, assign } from '@xstate/fsm';

type ConnectionStateContext = {
  backoff: number;
};

type ConnectionStateEvents =
  | { type: 'ERROR' }
  | { type: 'DISCONNECT' }
  | { type: 'CONNECT' }
  | { type: 'READY' }
  | { type: 'JOINED' }
  | { type: 'OFFLINE' }
  | { type: 'ONLINE' };

type ConntectionState =
  | {
      value: 'disconnected';
      context: ConnectionStateContext;
    }
  | {
      value: 'backoff';
      context: ConnectionStateContext;
    }
  | {
      value: 'connecting';
      context: ConnectionStateContext;
    }
  | {
      value: 'joining';
      context: ConnectionStateContext;
    }
  | {
      value: 'connected';
      context: ConnectionStateContext;
    }
  | {
      value: 'offline';
      context: ConnectionStateContext;
    };

export type ConnectionStateService = StateMachine.Service<
  ConnectionStateContext,
  ConnectionStateEvents,
  ConntectionState
>;

export type Actions = {
  connecting(): Promise<void>;
  disconnecting(): Promise<void>;
  joining(): Promise<void>;
};

export function createStateService(actions: Actions): ConnectionStateService {
  //
  // visualized FSM:
  // https://stately.ai/viz/d190fd40-a56c-44e3-ba9e-064023ce8232
  //
  const fsm = createMachine<
    ConnectionStateContext,
    ConnectionStateEvents,
    ConntectionState
  >({
    id: 'websockets',
    initial: 'disconnected',
    context: {
      backoff: 0,
    },
    states: {
      disconnected: {
        entry: assign({ backoff: 0 }),
        on: {
          CONNECT: 'connecting',
        },
      },
      backoff: {
        entry: assign({ backoff: (ctx) => ctx.backoff + 1 }),
        on: {
          CONNECT: 'connecting',
          OFFLINE: 'offline',
          DISCONNECT: 'disconnected',
        },
      },
      connecting: {
        on: {
          READY: 'joining',
          ERROR: 'backoff',
          OFFLINE: 'offline',
          DISCONNECT: 'disconnected',
        },
      },
      joining: {
        on: {
          JOINED: 'connected',
          ERROR: 'backoff',
          OFFLINE: 'offline',
          DISCONNECT: 'disconnected',
        },
      },
      connected: {
        entry: assign({ backoff: 0 }),
        on: {
          ERROR: 'backoff',
          OFFLINE: 'offline',
          DISCONNECT: 'disconnected',
        },
      },
      offline: {
        entry: assign({ backoff: 0 }),
        on: {
          ONLINE: 'connecting',
          DISCONNECT: 'disconnected',
        },
      },
    },
  });

  const service = interpret(fsm).start();

  // internal state logic
  service.subscribe(async (state) => {
    if (!state.changed) {
      return;
    }

    switch (state.value) {
      case 'backoff':
        await backoff(state.context.backoff - 1);
        service.send('CONNECT');
        break;
      case 'connecting':
        try {
          await actions.connecting();
          service.send('READY');
        } catch (e) {
          console.error(e);
          service.send('ERROR');
        }
        break;
      case 'joining':
        try {
          await actions.joining();
          service.send('JOINED');
        } catch (e) {
          console.error(e);
          service.send('ERROR');
        }
        break;
      case 'offline':
        await actions.disconnecting();
        break;
    }
  });

  return service;
}

async function backoff(n: number): Promise<void> {
  const backoffTime = ([1, 2, 4, 8, 16, 32][n] || 60) * 1000;

  await new Promise((resolve) => setTimeout(resolve, backoffTime));
}
